import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, ChevronRight, CircleAlert, CircleCheck, CircleDot, Info, RotateCw,
} from 'lucide-react';
import {
  Card, CardHeader, Input, Field, Select, EmptyState, ErrorNotice, Loading, Pagination,
} from '../../components/ui/index.js';
import { PageHero } from '../../components/PageHero.jsx';
import { useResource } from '../../hooks/useApi.js';
import { formatMoney, formatTimestamp, formatExactTimestamp, formatNumber, truncateId } from '../../lib/format.js';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * Everything the organisation did, in one stream.
 *
 * The product already kept four kinds of history — an audit log, a record per
 * charge attempt, a timeline on each payment and one per webhook delivery — and
 * none of them answered the question anybody actually asks, which is "what
 * happened to my money today, and where did it go wrong". This screen is that
 * question, and it is deliberately read-only: a record somebody can edit is not
 * a record.
 */
const CATEGORIES = [
  { value: '', label: 'Everything' },
  { value: 'payment', label: 'Payments' },
  { value: 'refund', label: 'Refunds' },
  { value: 'pos', label: 'Till' },
  { value: 'webhook', label: 'Webhooks' },
  { value: 'provider', label: 'Providers' },
  { value: 'settings', label: 'Settings' },
  { value: 'auth', label: 'Sign-ins' },
  { value: 'admin', label: 'Platform team' },
];

const OUTCOMES = [
  { value: '', label: 'Any outcome' },
  { value: 'failure', label: 'Only what failed' },
  { value: 'success', label: 'Only what worked' },
  { value: 'pending', label: 'Still waiting' },
  { value: 'info', label: 'Changes' },
];

const OUTCOME_STYLE = {
  success: { icon: CircleCheck, chip: 'chip-success', label: 'Worked' },
  failure: { icon: CircleAlert, chip: 'chip-danger', label: 'Failed' },
  pending: { icon: CircleDot, chip: 'chip-warning', label: 'Waiting' },
  info: { icon: Info, chip: 'chip-neutral', label: 'Change' },
};

/** Who did it, in the fewest words that still identify them. */
function actorLabel(actor) {
  if (!actor) return 'System';
  if (actor.label) return actor.label;

  return {
    user: 'A team member',
    admin: 'Platform team',
    api_key: 'Your API key',
    customer: 'The customer',
    system: 'System',
    anonymous: 'Someone signed out',
  }[actor.kind] ?? 'System';
}

/** The subject, linked where there is somewhere to go. */
function Subject({ subject }) {
  if (!subject?.id) return null;

  const body = (
    <span className="font-mono text-xs text-muted">
      {subject.label ? `${subject.label} · ` : ''}{truncateId(subject.id)}
    </span>
  );

  return subject.kind === 'payment'
    ? <Link to={`/payments/${subject.id}`} className="hover:underline">{body}</Link>
    : body;
}

/**
 * One event, expandable.
 *
 * Collapsed it reads as a sentence; opened it shows the provider code, the
 * attempt number, how long the call took and the raw detail — which is the
 * difference between "a payment failed" and "the gateway rejected the request
 * because the transaction key is wrong".
 */
function Row({ event }) {
  const [open, setOpen] = useState(false);
  const style = OUTCOME_STYLE[event.outcome] ?? OUTCOME_STYLE.info;
  const Icon = style.icon;

  const hasDetail = Boolean(
    event.detail || event.error || event.provider || event.attempt
    || event.durationMs != null || event.requestId,
  );

  return (
    <div className="border-b border-line-soft last:border-0">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        aria-expanded={hasDetail ? open : undefined}
        className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
          hasDetail ? 'hover:bg-surface-2' : 'cursor-default'
        }`}
      >
        <span className={`mt-0.5 shrink-0 ${
          event.outcome === 'failure' ? 'text-red-600'
            : event.outcome === 'success' ? 'text-emerald-600'
              : event.outcome === 'pending' ? 'text-amber-600' : 'text-faint'
        }`}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm text-ink">{event.summary}</span>
            {event.amount != null && (
              <span className="tabular text-sm font-medium text-ink">
                {formatMoney(event.amount, event.currency ?? 'USD')}
              </span>
            )}
          </span>

          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
            <span>{actorLabel(event.actor)}</span>
            <span aria-hidden="true">·</span>
            <span title={formatExactTimestamp(event.at)}>{formatTimestamp(event.at)}</span>
            {event.subject?.id && (
              <>
                <span aria-hidden="true">·</span>
                <Subject subject={event.subject} />
              </>
            )}
            {event.attempt > 1 && (
              <>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1">
                  <RotateCw className="h-3 w-3" aria-hidden="true" />
                  attempt {event.attempt}
                </span>
              </>
            )}
          </span>
        </span>

        {hasDetail && (
          <ChevronRight
            className={`mt-0.5 h-4 w-4 shrink-0 text-faint transition-transform ${open ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="space-y-3 bg-surface-2 px-4 py-3.5">
              {event.error?.message && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                  <p className="text-xs font-medium text-red-800">
                    {event.error.code ?? 'error'}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-red-700">{event.error.message}</p>
                </div>
              )}

              <dl className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
                <Detail label="Event">{event.action}</Detail>
                <Detail label="When">{formatExactTimestamp(event.at)}</Detail>
                {event.provider && <Detail label="Provider">{event.provider}</Detail>}
                {event.attempt != null && <Detail label="Attempt">{event.attempt}</Detail>}
                {event.durationMs != null && <Detail label="Took">{formatNumber(event.durationMs)} ms</Detail>}
                {event.actor?.ip && <Detail label="From">{event.actor.ip}</Detail>}
                {event.requestId && <Detail label="Request">{event.requestId}</Detail>}
                {event.subject?.id && <Detail label="Subject">{event.subject.id}</Detail>}
              </dl>

              {event.detail && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-faint">Detail</p>
                  {/*
                    * Redacted on the way in, by key name (SE-8) — there is no
                    * card number or credential in here to leak.
                    */}
                  <pre className="overflow-x-auto rounded-lg border border-line bg-surface px-3 py-2.5 text-[11px] leading-relaxed text-ink-soft">
                    {JSON.stringify(event.detail, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="min-w-0 break-all font-mono text-ink-soft">{children}</dd>
    </div>
  );
}

export function ActivityPage() {
  const [category, setCategory] = useState('');
  const [outcome, setOutcome] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => { setApplied(search.trim()); setSkip(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => ({
    limit: 50,
    skip,
    ...(category ? { category } : {}),
    ...(outcome ? { outcome } : {}),
    ...(applied ? { search: applied } : {}),
  }), [category, outcome, applied, skip]);

  const { data, error, loading, refetch } = useResource('/dashboard/activity', { params });
  const summary = useResource('/dashboard/activity/summary');

  const filtered = Boolean(category || outcome || applied);

  return (
    <>
      <PageHero
        icon={Activity}
        chip="Workspace"
        title="Activity"
        blurb="Everything that happened here: every payment and what the gateway said, every retry, every setting somebody changed and every sign-in. Kept for 90 days."
      />

      {summary.data && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="chip-neutral">
            {formatNumber(summary.data.total)} in the last 24 hours
          </span>
          {summary.data.failures > 0 && (
            <button type="button" className="chip-danger" onClick={() => { setOutcome('failure'); setSkip(0); }}>
              {formatNumber(summary.data.failures)} failed
            </button>
          )}
        </div>
      )}

      <Card>
        <CardHeader
          title="Event stream"
          blurb={data ? `${formatNumber(data.total)} event${data.total === 1 ? '' : 's'}` : undefined}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40">
                <Field>
                  {(props) => (
                    <Select {...props} value={category}
                      onChange={(e) => { setCategory(e.target.value); setSkip(0); }}>
                      {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </Select>
                  )}
                </Field>
              </div>
              <div className="w-44">
                <Field>
                  {(props) => (
                    <Select {...props} value={outcome}
                      onChange={(e) => { setOutcome(e.target.value); setSkip(0); }}>
                      {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  )}
                </Field>
              </div>
              <div className="w-52">
                <Field>
                  {(props) => (
                    <Input {...props} type="search" value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Payment id, or any text" />
                  )}
                </Field>
              </div>
            </div>
          }
        />

        {loading && <Loading label="Loading activity…" />}
        {error && <ErrorNotice error={error} onRetry={refetch} className="m-4" />}

        {!loading && !error && data?.events?.length === 0 && (
          <EmptyState
            title={filtered ? 'Nothing matches those filters' : 'Nothing has happened yet'}
            blurb={
              filtered
                ? 'Try a wider category, or clear the search.'
                : 'Take a payment, change a setting or sign in again, and it will show up here.'
            }
          />
        )}

        {!loading && !error && data?.events?.length > 0 && (
          <>
            <div>
              {data.events.map((event) => <Row key={event.id} event={event} />)}
            </div>
            <Pagination total={data.total} limit={data.limit} skip={data.skip} onChange={setSkip} />
          </>
        )}
      </Card>
    </>
  );
}

export default ActivityPage;
